import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import DataTable from '../common/DataTable';
import { formatDate, today, firstOfMonth } from '../../utils/helpers';

const reportTypes = [
  { key: 'dayDetails', label: 'Day wise Production Details' },
  { key: 'daySummary', label: 'Day wise Production Summary' },
  { key: 'consDetails', label: 'Item Consumption Details' },
  { key: 'consSummary', label: 'Item Consumption Summary' },
];

export default function ProductionReports() {
  const { state } = useApp();
  const [report, setReport] = useState('dayDetails');
  const [startDate, setStartDate] = useState(firstOfMonth());
  const [endDate, setEndDate] = useState(today());

  const itemName = (id) => state.items.find(i => i.id === id)?.name || '-';
  const itemUnit = (id) => state.items.find(i => i.id === id)?.unit || '';
  const sumQty = (lines) => (lines || []).reduce((s, l) => s + (Number(l.qty) || 0), 0);
  const summarize = (lines) => (lines || []).map(l => `${itemName(l.itemId)} × ${l.qty}`).join(', ');

  // Productions within the selected date range
  const productions = useMemo(() => {
    return state.productions.filter(p => {
      if (startDate && (p.date || '') < startDate) return false;
      if (endDate && (p.date || '') > endDate) return false;
      return true;
    });
  }, [state.productions, startDate, endDate]);

  // One row per production entry
  const dayDetails = useMemo(() => productions
    .slice()
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .map(p => ({
      id: p.id,
      date: p.date,
      productionNo: p.productionNo,
      batchNo: p.batchNo || '-',
      inputs: summarize(p.inputs),
      outputs: summarize(p.outputs),
      inputQty: sumQty(p.inputs),
      outputQty: sumQty(p.outputs),
    })),
    [productions] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Grouped by date (produced items aggregated per day)
  const daySummary = useMemo(() => {
    const map = {};
    productions.forEach(p => {
      const d = p.date || '-';
      if (!map[d]) map[d] = { date: d, count: 0, outputQty: 0, itemQty: {} };
      map[d].count += 1;
      map[d].outputQty += sumQty(p.outputs);
      (p.outputs || []).forEach(l => {
        map[d].itemQty[l.itemId] = (map[d].itemQty[l.itemId] || 0) + (Number(l.qty) || 0);
      });
    });
    return Object.values(map)
      .map(r => ({
        ...r,
        items: Object.entries(r.itemQty).map(([id, q]) => `${itemName(id)} × ${q}`).join(', '),
      }))
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [productions]); // eslint-disable-line react-hooks/exhaustive-deps

  // One row per consumed (input) line
  const consDetails = useMemo(() => {
    const rows = [];
    productions.forEach(p => {
      (p.inputs || []).forEach(l => {
        rows.push({
          id: `${p.id}-${l.itemId}`,
          date: p.date,
          productionNo: p.productionNo,
          batchNo: p.batchNo || '-',
          item: itemName(l.itemId),
          qty: Number(l.qty) || 0,
          uom: l.uom || itemUnit(l.itemId),
        });
      });
    });
    return rows.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  }, [productions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Consumed lines grouped by item
  const consSummary = useMemo(() => {
    const map = {};
    productions.forEach(p => {
      (p.inputs || []).forEach(l => {
        if (!map[l.itemId]) map[l.itemId] = { item: itemName(l.itemId), uom: l.uom || itemUnit(l.itemId), qty: 0, count: 0 };
        map[l.itemId].qty += Number(l.qty) || 0;
        map[l.itemId].count += 1;
      });
    });
    return Object.values(map).sort((a, b) => b.qty - a.qty);
  }, [productions]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderReport = () => {
    switch (report) {
      case 'dayDetails':
        return <DataTable
          columns={[
            { key: 'date', label: 'Date', sortable: true, render: v => formatDate(v), exportValue: r => formatDate(r.date) },
            { key: 'productionNo', label: 'Production No', sortable: true },
            { key: 'batchNo', label: 'Batch No' },
            { key: 'outputs', label: 'Outputs (Produced)' },
            { key: 'outputQty', label: 'Out Qty', align: 'right', sortable: true },
          ]}
          data={dayDetails}
          searchFields={['productionNo', 'batchNo', 'outputs']}
          totals={(rows) => ({ outputQty: rows.reduce((s, r) => s + r.outputQty, 0) })}
        />;

      case 'daySummary':
        return <DataTable
          columns={[
            { key: 'date', label: 'Date', sortable: true, render: v => formatDate(v), exportValue: r => formatDate(r.date) },
            { key: 'items', label: 'Items Produced' },
            { key: 'count', label: 'No. of Productions', align: 'right', sortable: true },
            { key: 'outputQty', label: 'Total Output Qty', align: 'right', sortable: true },
          ]}
          data={daySummary}
          searchFields={['date', 'items']}
          totals={(rows) => ({
            count: rows.reduce((s, r) => s + r.count, 0),
            outputQty: rows.reduce((s, r) => s + r.outputQty, 0),
          })}
        />;

      case 'consDetails':
        return <DataTable
          columns={[
            { key: 'date', label: 'Date', sortable: true, render: v => formatDate(v), exportValue: r => formatDate(r.date) },
            { key: 'productionNo', label: 'Production No', sortable: true },
            { key: 'batchNo', label: 'Batch No' },
            { key: 'item', label: 'Consumed Item', sortable: true },
            { key: 'qty', label: 'Qty', align: 'right', sortable: true },
            { key: 'uom', label: 'UOM' },
          ]}
          data={consDetails}
          searchFields={['item', 'productionNo', 'batchNo']}
          totals={(rows) => ({ qty: rows.reduce((s, r) => s + r.qty, 0) })}
        />;

      case 'consSummary':
        return <DataTable
          columns={[
            { key: 'item', label: 'Consumed Item', sortable: true },
            { key: 'uom', label: 'UOM' },
            { key: 'qty', label: 'Total Consumed Qty', align: 'right', sortable: true },
            { key: 'count', label: 'Times Used', align: 'right', sortable: true },
          ]}
          data={consSummary}
          searchFields={['item']}
          totals={(rows) => ({ qty: rows.reduce((s, r) => s + r.qty, 0), count: rows.reduce((s, r) => s + r.count, 0) })}
        />;

      default: return null;
    }
  };

  return (
    <div>
      <h1 className="page-title mb-6">Production Reports</h1>
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[260px]">
            <label className="label">Report Type</label>
            <select className="input" value={report} onChange={e => setReport(e.target.value)}>
              {reportTypes.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">From Date</label>
            <input className="input" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="label">To Date</label>
            <input className="input" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
      </div>
      <div className="card">{(() => {
        const el = renderReport();
        return el && el.type === DataTable ? React.cloneElement(el, { exportFilename: `Production_${report}` }) : el;
      })()}</div>
    </div>
  );
}
